import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RichTextEditor } from '@/components/rich-text';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';

interface CourseAssignmentAnswerSheetProps {
  open: boolean;
  selectedAssignmentName: string;
  answerDraft: string;
  answerAttachment: File | null;
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
  onClose: () => void;
  onAnswerDraftChange: (value: string) => void;
  onProvideGetSerializedState: (getter: () => string) => void;
  onAnswerAttachmentChange: (file: File | null) => void;
  onCancel: () => void;
  onSubmit: () => void;
}

const CourseAssignmentAnswerSheet = ({
  open,
  selectedAssignmentName,
  answerDraft,
  answerAttachment,
  isSubmitting,
  onOpenChange,
  onClose,
  onAnswerDraftChange,
  onProvideGetSerializedState,
  onAnswerAttachmentChange,
  onCancel,
  onSubmit,
}: CourseAssignmentAnswerSheetProps) => {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full p-0 sm:max-w-[min(97vw,1250px)]" showClose={false}>
        <SheetHeader className="border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-muted"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>
            <SheetTitle>{selectedAssignmentName}</SheetTitle>
          </div>
        </SheetHeader>

        <div className="flex h-[calc(100vh-64px)] min-h-0 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
            <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
              Note: Submission requires either an answer or an attachment.
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Answer</label>
              <div className="h-[480px] overflow-hidden rounded-xl border bg-background/60 p-1">
                <RichTextEditor
                  value={answerDraft}
                  onChange={onAnswerDraftChange}
                  placeholder="Add answer"
                  className="[&_.editor-container]:m-0 [&_.editor-container]:h-full [&_.editor-inner]:h-full [&_.editor-scroller]:max-h-full [&_.editor-scroller]:overflow-y-auto [&_.editor-input]:min-h-full"
                  minHeight={0}
                  onProvideGetSerializedState={onProvideGetSerializedState}
                />
              </div>
            </div>

            <div className="mt-5 space-y-2">
              <label className="text-sm font-medium">Attachment</label>
              <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed bg-muted/20 px-4 py-10 text-center">
                <p className="text-sm font-medium text-muted-foreground">Drag and drop files here</p>
                <p className="mt-1 text-xs text-muted-foreground">Supported files: PDF, PNG, JPEG.</p>
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0] || null;
                    onAnswerAttachmentChange(file);
                  }}
                />
              </label>
              {answerAttachment ? (
                <div className="rounded-md border bg-background px-3 py-2 text-sm">{answerAttachment.name}</div>
              ) : null}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t bg-background px-4 py-3">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={onSubmit} disabled={isSubmitting}>
              Submit
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CourseAssignmentAnswerSheet;
